import {
  GoogleMap,
  HeatmapLayer,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { AxiosError } from "axios";
import React, { useRef } from "react";
import IMAGES from "../../assets";
import DetailCard from "../../components/DetailCard/DetailCard";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import Spinner from "../../components/Spinner/Spinner";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {
  ErrorResponse,
  IItems,
  IPermisesList,
} from "../../interfaces/PermisesListResponse";
import ENDPOINTS from "../../services/Endpoints";
import axiosInstance from "../../services/axiosInstance";
import { CONSTANTS } from "../../utils/Constants";
import { getColorForValue, MAP_API_KEY, REACT_MAP_LIBRARIE } from "../../utils/Helpers";
import { COLORS } from "../../utils/Colors";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleUp, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import NDVIMap from "../../components/NDVIMap/NDVIMap";
import AgroComponent from "../../components/AgroComponent/AgroComponent";

const Home = () => {
  const [selectedCoords, setSelectedCoords] = React.useState<{ lat: number; lng: number, customerAlias: string, customerId: string } | null>(null);
  const [showCustomers, setShowCustomers] = React.useState<boolean>(true)
  const [showExpansion, setShowExpansion] = React.useState<boolean>(false)
  const [showAnalyze, setShowAnalyze] = React.useState<boolean>(false)
  const [googleMap, setMap] = React.useState<google.maps.Map | null>(null);
  const [expanded, setExpanded] = React.useState<boolean>(true)
  const { width, height } = useWindowDimensions();
  const refMap = useRef<GoogleMap | null>(null);
  const [polyid, setPolyid] = React.useState<string | null>(null)
  const [heatLayerVisible, setHeatLayerVisible] = React.useState(true);

  const [loading, setLoading] = React.useState(false);
  const [center] = React.useState({ lat: 7.7832, lng: 24.5085 });

  const [permisesList, setPermisesList] = React.useState<IPermisesList>();
  const [heatMapData, setheatMapData] = React.useState<{ lat: number; lng: number }[]>();

  const [selectedPermise, setselectedPermise] = React.useState<IItems>();
  const [selectedPremiseError, setSelectedPremiseError] =
    React.useState<ErrorResponse>();

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MAP_API_KEY || "",
    libraries: REACT_MAP_LIBRARIE || ['drawing', 'visualization'],
  });

  const handleLocationSelect = (lat: string, lng: string, customerAlias: string, customerId: string) => {
    if (lat && lng) {
      setSelectedCoords({ lat: Number(lat!), lng: Number(lng), customerAlias: customerAlias, customerId: customerId });
      setShowAnalyze(true)
    }
  };

  const handlePolygonSelect = (polygonId: string | null) => {
    if (polygonId) {
      setPolyid(polygonId)
    } else {
      setPolyid(null)
    }
  }


  const getSelectedpermiseDetails = (id: string) => {
    setLoading(true);
    axiosInstance
      .get(ENDPOINTS.getPermisesList + "/" + id, {
        headers: {
          accept: "application/json",
          employee_id: 21,
        },
      }).then((res) => {
        setselectedPermise(res.data);
      }).catch((err: AxiosError<ErrorResponse>) => {
        setSelectedPremiseError(err.response?.data);
      }).finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (heatMapData) {
      setTimeout(() => {
        refMap.current?.state?.map?.setZoom(10);
        setTimeout(() => {
          refMap.current?.state?.map?.setZoom(4);
        }, 500);
      }, 500);
    }
  }, [heatMapData]);

  React.useEffect(() => {
    setLoading(true);
    axiosInstance.get(ENDPOINTS.getPermisesList, {
      params: {
        page: 1,
        size: 1000
      },
      headers: {
        employee_id: 21,
      },
    }).then((res) => {
      const list: IPermisesList = res.data;
      const latlng: React.SetStateAction<{ lat: number; lng: number }[]> = [];
      if (list.items.length > 0) {
        setPermisesList(list);
        list.items.forEach((item) => {
          if (item.detail?.latitude && item.detail?.longitude) {
            latlng.push({
              lat: item.detail.latitude,
              lng: item.detail.longitude,
            });
          }
        });
        setheatMapData(latlng);
        setLoading(false);
      }
    }).catch((err) => {
      setLoading(false);
      console.log(err);
    });
  }, []);

  const handleZoomChanged = () => {
    if (refMap.current) {
      const newZoom = refMap.current?.state.map?.getZoom();
      if (newZoom) {
        if (newZoom < 10) {
          setHeatLayerVisible(true);
        } else {
          setHeatLayerVisible(false);
        }
      }
    }
  };

  React.useEffect(() => {
    if (googleMap && isLoaded) {
      loadGeoJsonLayer(googleMap);
    }
  }, [isLoaded, googleMap, showExpansion]);

  const loadGeoJsonLayer = async (map: google.maps.Map) => {
    if (showExpansion) {
      if (map.data.getFeatureById('geojson-layer')) return;
      try {
        const response = await fetch(process.env.REACT_APP_GEO_JSON!);
        const geoJson = await response.json();
        map.data.addGeoJson(geoJson);
        map.data.setStyle((feature: google.maps.Data.Feature) => ({
          fillColor: getColorForValue(feature.getProperty('VALUE') as number),
          strokeColor: getColorForValue(feature.getProperty('VALUE') as number),
          fillOpacity: 0.2,
          strokeOpacity: 0.5,
          strokeWeight: 1,
        }));
      } catch (error) {
        console.error('Error loading GeoJSON:', error);
      }
    } else {
      map.data.forEach((feature) => map.data.remove(feature)); // Remove all features efficiently
    }
  };

  //031926 468189 77ACA2 9DBEBB

  const renderHeatLayer = React.useCallback(() => {
    if (heatMapData && showCustomers === true) {
      if (heatMapData.length > 0) {
        return (
          <HeatmapLayer
            onLoad={(_res) => { }}
            onUnmount={(_res) => { }}
            options={{
              radius: 30,
              opacity: heatLayerVisible ? 1 : 0,
              gradient: [
                "rgba(3, 25, 38, 0)",  // Transparent start
                "rgba(3, 25, 38, 1)",  // #031926
                "rgba(70, 129, 137, 1)", // #468189
                "rgba(119, 172, 162, 1)", // #77ACA2
                "rgba(157, 190, 187, 1)"  // #9DBEBB
              ]
            }}
            data={heatMapData.map((point) => new window.google.maps.LatLng(point.lat, point.lng))}
          />
        );
      } else {
        return null;
      }
    }
  }, [heatLayerVisible, showCustomers]);

  const renderMarker = React.useCallback(() => {
    if (permisesList && showCustomers)
      return permisesList.items.map((item: IItems, index: number) => {
        if (item.detail) {
          if (item.detail.latitude && item.detail.latitude) {
            return (
              <Marker
                icon={{
                  scaledSize: new window.google.maps.Size(25, 25),
                  url: "http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png",
                }}
                opacity={heatLayerVisible ? 0 : 1}
                clickable
                onClick={() => getSelectedpermiseDetails(item.id)}
                key={index}
                position={{
                  lat: item.detail.latitude,
                  lng: item.detail.longitude,
                }}
              />
            );
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
  }, [heatLayerVisible, permisesList, showCustomers]);

  if (loadError) return <PageWrapper title="Home">
    <div>
      Error loading maps
    </div>;
  </PageWrapper>

  if (!isLoaded) return <div>
    <div>
      Error loading maps
    </div>;
  </div>;


  return (
    <PageWrapper title="Home">
      {isLoaded ? (
        <>
          {loading === true && <div className="absolute w-full h-full flex z-20 items-center justify-center bg-white/20">
            <Spinner />
          </div>}
          <GoogleMap
            mapContainerStyle={{
              width,
              height,
              borderRadius: 10,
            }}
            center={center}
            zoom={4}
            mapTypeId="hybrid"
            onLoad={(mapInstance) => {
              setMap(mapInstance)
              mapInstance.setZoom(4);
            }}

            ref={refMap}
            options={{
              fullscreenControlOptions: {
                position: window.google.maps.ControlPosition.TOP_LEFT,
              },
              mapTypeControl: true,
              mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.DEFAULT,
                position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
              },
              minZoom: 3,
            }}
            onZoomChanged={handleZoomChanged}>
            {renderMarker()}
            {renderHeatLayer()}
            <img
              src={IMAGES.logo}
              className="z-10 absolute top-3 right-5 w-1/6"
              alt={CONSTANTS.appTitle}
            />

            <div className="flex absolute z-10 top-16 left-10 flex-row">
              <div
                className="flex flex-col bg-blue-50 w-96 h-5/6 gap-2 rounded-xl items-center">
                <DetailCard
                  loading={false}
                  selectedPermise={selectedPermise}
                  selectedPremiseError={selectedPremiseError}
                  onSelectLocation={handleLocationSelect}
                />
              </div>
              <div
                className={`ml-4 bg-slate-100 w-96  rounded-xl ${expanded === true ? 'max-h-40' : 'max-h-12'} items-cente`}
                style={{
                  boxShadow:
                    "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
                  height: height * 0.9,
                  scrollbarWidth: "thin",
                }}>
                <div
                  className={`flex ${expanded === true ? 'h-1/3' : 'h-full'} justify-around items-center rounded-b-2xl rounded-t-xl w-full`}
                  style={{ background: COLORS.Brand }}
                >
                  <div className="flex w-1/2 justify-end text-end flex-1 text-2xl font-semibold text-white">
                    {CONSTANTS.layers}
                  </div>
                  <div className="flex w-1/2 justify-end items-center text-2xl hover:cursor-pointer" >
                    {expanded === true && <FontAwesomeIcon onClick={() => setExpanded(prev => !prev)} className="w-5 h-5 pr-2" color="white" icon={faAngleUp} />}
                    {expanded === false && <FontAwesomeIcon onClick={() => setExpanded(prev => !prev)} className="w-5 h-5 pr-2" color="white" icon={faAngleDown} />}
                  </div>
                </div>

                {expanded === true && <div
                  className="flex flex-col flex-1 justify-center bg-white rounded-xl drop-shadow-sm pl-4" style={{ height: "100%" }}>


                  <div className="flex items-center mt-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={showCustomers === true}
                        onChange={() => setShowCustomers(prev => !prev)}
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:left-1 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        SunCulture Customers
                      </span>
                    </label>
                  </div>

                  <div className="flex items-center mt-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={showExpansion === true}
                        onChange={() => setShowExpansion(prev => !prev)}
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:left-1 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                      <div className="ml-2 text-sm font-medium text-gray-700">

                        <div className="relative group">
                          <button className="rounded px-1 hover:cursor-pointer">
                            Predicted expansion  <FontAwesomeIcon icon={faQuestionCircle} />
                          </button>
                          <div className="absolute left-1/2 -translate-x-1/2 top-15 bg-slate-100 text-black text-xs px-6 py-2 rounded opacity-0 group-hover:opacity-100 transition-opacity w-80 z-20">
                            This map represents the likelihood of an area being suitable for SunCulture’s current product line. The analysis is based on extrapolating insights from our existing customer base using key environmental and demographic factors, including:
                            <ul className="list-disc">
                              <li><span className="font-semibold">Population density</span> – to estimate market demand and accessibility.</li>
                              <li><span className="font-semibold">Water table characteristics </span>– to assess groundwater availability and feasibility of pumping.</li>
                              <li><span className="font-semibold">Presence of agriculture</span>– to identify areas with active farming communities.</li>
                              <li><span className="font-semibold">Solar irradiation rates</span>– to determine the viability of solar-powered solutions.</li>
                            </ul>
                            This tool helps visualize potential expansion opportunities and optimize deployment strategies.

                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
                }
                {showAnalyze === true && <div
                  className="flex w-96 h-4/6 mt-2 rounded-xl items-center flex-col">

                  <NDVIMap isLoaded={isLoaded}
                    lat={selectedCoords?.lat!}
                    lng={selectedCoords?.lng!}
                    customerAlias={selectedCoords?.customerAlias!}
                    customerId={selectedCoords?.customerId!}
                    onSelectPolygonId={handlePolygonSelect}
                    setShowAnalyze={setShowAnalyze} />
                </div>}
              </div>
            </div>


            {polyid !== null && <AgroComponent polyid={polyid} lat={selectedCoords?.lat.toString()!} lng={selectedCoords?.lng?.toString()!} setPolyid={setPolyid} />}

          </GoogleMap>
        </>
      ) : (
        <div className="flex h-screen w-screen justify-center items-center">
          <Spinner />
        </div>
      )}
    </PageWrapper>
  );
};

export default Home;